<script>
import { fade } from "svelte/transition";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import * as m from "@app/paraglide/messages.js";
import _EventGroup from "@cclib/classes/eventGroup.js";

/* The modal component is only mounted while it is open, so the
   source group and default track names can be captured at init. */
const sourceGroup =
    typeof projectState.selected === "number"
        ? eventGroupState[projectState.selected]
        : undefined;

function getTrackCount(eventGroup) {
    if (!eventGroup) return 0;
    if (Array.isArray(eventGroup.tracks) && eventGroup.tracks.length >= 2) {
        return eventGroup.tracks.length;
    }
    return Math.max(
        2,
        ...eventGroup.events.map((event) => event.track || 1),
    );
}

let trackNames = $state(
    sourceGroup && sourceGroup.type === "multiview"
        ? Array.from({ length: getTrackCount(sourceGroup) }, (_, i) => {
              return sourceGroup.tracks?.[i]?.name || `${sourceGroup.name} - ${m.multiview_track()} ${i + 1}`;
          })
        : [],
);

function splitMultiviewGroup() {
    try {
        if (!sourceGroup || sourceGroup.type !== "multiview") {
            throw new Error("No multiview event group selected.");
        }

        const trackCount = trackNames.length;

        for (let trackIndex = 0; trackIndex < trackCount; trackIndex++) {
            const trackNumber = trackIndex + 1;
            const clone = JSON.parse(JSON.stringify(sourceGroup));

            clone.type = "subtitle";
            clone.name = trackNames[trackIndex] || `${sourceGroup.name} - ${m.multiview_track()} ${trackNumber}`;
            clone.language = sourceGroup.tracks?.[trackIndex]?.language || sourceGroup.language;
            clone.rtl = sourceGroup.tracks?.[trackIndex]?.rtl ?? sourceGroup.rtl;
            clone.tracks = [];
            clone.selected = [];
            clone.scrollPosition = 0;
            clone.locked = false;
            /* Multiview events are already ordered by time, so filtering preserves order */
            clone.events = sourceGroup.events
                .filter((event) => (event.track || 1) === trackNumber)
                .map((event) => ({ ...event, track: 1 }));

            eventGroupState.push({ ...(new _EventGroup(clone)) });
        }

        insertHistoryAction({
            name: "split multiview group",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        toast.push(m.split_multiview_success({ count: trackCount }), {
            classes: ["toast-success"],
        });

        hideModal();
    } catch (err) {
        console.log(err, err.message);

        toast.push(m.split_multiview_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'splitMultiviewGroup' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="splitMultiviewGroupModalTitle"
    aria-describedby="splitMultiviewGroupModalDesc"
    tabindex="-1"
    id="SplitMultiviewGroupModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="splitMultiviewGroupModalTitle">
                    {m.split_multiview_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closeSplitMultiviewGroupBtn"
                    aria-label={m.split_multiview_close_aria_label()}
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <p id="splitMultiviewGroupModalDesc" class="form-text">
                    {m.split_multiview_description()}
                </p>
                <form onsubmit={(submitEvent) => { submitEvent.preventDefault(); splitMultiviewGroup(); }}>
                    {#each trackNames as trackName, trackIndex (trackIndex)}
                        <div class="mb-3">
                            <label class="form-label" for="splitMultiviewTrackName-{trackIndex}">
                                <span class="badge bg-primary">{m.split_multiview_track_label({ track: trackIndex + 1 })}</span>
                                {m.split_multiview_group_name_label()}
                            </label>
                            <input
                                class="form-control"
                                id="splitMultiviewTrackName-{trackIndex}"
                                type="text"
                                required
                                bind:value={trackNames[trackIndex]}>
                        </div>
                    {/each}
                    <button
                        class="btn btn-primary float-end"
                        id="splitMultiviewGroupBtn"
                        type="button"
                        disabled={trackNames.some((name) => !name)}
                        onclick={splitMultiviewGroup}
                        aria-label={m.split_multiview_button()}>{m.split_multiview_button()}</button>
                </form>
            </div>
        </div>
    </div>
</div>
